import React, { useMemo } from 'react';
import { useRememberedState, useRememberedScroll } from '../../hooks/useRemembered';
import { UserProfile, ReportMetadata, ServiceItem } from '../../types';
import { contractedQuantity, isNepService } from '../../services/nepPosition';
import { istPlanungsleistung } from '../../services/leistungsPhase';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  ExternalLink, 
  Filter, 
  Check, 
  ShieldAlert, 
  BadgeCheck, 
  Search, 
  AlertCircle, 
  Calendar, 
  Clock,
  User,
  FileDown,
  Info
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getNormalizedServices } from '../../services/projectLv';
import { istUebungsprojekt } from '../../services/uebung/uebungService';
import { flatCoverageHints } from '../../services/flatRateLogic';
import type { ListProject } from '../../services/listProject';

interface Props {
  projects: ListProject[];
  users: UserProfile[];
  onOpenProject: (project: ListProject, isReadOnly: boolean) => void;
}

// Altbestand: fruehe Projekte trugen den Beginn direkt in `metadata.startDate`.
// Das Feld steht nicht (mehr) in `ReportMetadata`, der Rueckfall bleibt fuer
// solche Bloecke erhalten.
const legacyMetaStartDate = (meta?: ReportMetadata): string | undefined =>
  (meta as { startDate?: string } | undefined)?.startDate;

interface DueDateInfo {
  date: string;
  isComputed: boolean;
  label: string;
  isOverdue: boolean;
}

interface OpenPositionRow {
  id: string; // ServiceItem id
  lvPosition: string;
  title: string;
  projectNumber: string;
  projectName: string;
  coordinatorId: string;
  coordinatorName: string;
  startDate: string; // Project start date
  dueDateObj: DueDateInfo;
  cost: number; // contracted total cost
  projectRaw: ListProject;
  category?: string;
  assignment?: string;
  /**
   * MONATSPAUSCHALE: gesetzt, wenn diese Position von einer Pauschale gedeckt
   * ist — der Hinweistext benennt die deckende Pauschale. Eine gedeckte
   * Position traegt dann KEIN eigenes Geld in dieser Uebersicht (cost 0): ihr
   * Betrag steckt in der Pauschalposition, die hier selbst gelistet wird —
   * beide zu summieren forderte dasselbe Geld doppelt.
   */
  flatHint?: string;
}

// Frueher stand hier eine EIGENE Kopie von getNormalizedServices ohne den
// Deckungsstempel der Monatspauschale — gedeckte Positionen erschienen damit
// weiter als offenes Geld. Jetzt liest die Ansicht denselben Pfad wie die
// Buchhaltung (projectLv.getNormalizedServices).
export const AdminOpenPositionsView: React.FC<Props> = ({ projects: alleProjekte, users, onOpenProject }) => {
    // ÜBUNGSPROJEKTE FALLEN IN KEINE AUSWERTUNG (docs/lernzentrum-tutorial-plan.md,
    // 1.11 und 8.2). Gefiltert wird hier und nicht beim Laden, weil die Liste als
    // Prop ankommt und dieselbe Liste zugleich die Projektverwaltung speist, die
    // Übungsprojekte sehr wohl zeigt.
    const projects = useMemo(() => alleProjekte.filter(p => !istUebungsprojekt(p)), [alleProjekte]);

  const calculateDueDate = (s: ServiceItem, p: ListProject): DueDateInfo => {
    const meta = p.data?.metadata as ReportMetadata | undefined;
    const explicitDate = s.dueDate || '';
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (explicitDate) {
      const dVal = new Date(explicitDate);
      dVal.setHours(0, 0, 0, 0);
      const overdue = dVal.getTime() < now.getTime() && s.status !== 'done' && s.status !== 'billed';
      return {
        date: explicitDate,
        isComputed: false,
        label: 'Explizit',
        isOverdue: overdue
      };
    }

    // Determine if it is part of planning/design (Entwurf)
    //
    // `einweisung` gehoert hier BEWUSST NICHT hinein: die Einweisung in den
    // SiGe-Plan findet auf der Baustelle waehrend der AUSFUEHRUNG statt. Die
    // Planungszeile leitet daraus einen Termin von "Projektstart minus 14
    // Tage" ab — eine Einweisung waere damit vom ersten Tag an ueberfaellig,
    // obwohl sie erst noch stattfinden muss.
    const isPlanning = istPlanungsleistung(s);
     
    if (isPlanning) {
      const projectStart = p.start_date || meta?.globalData?.startDate || legacyMetaStartDate(meta);
      if (projectStart) {
        try {
          const startDateObj = new Date(projectStart);
          // Subtract 14 days (2 weeks)
          const computedDateObj = new Date(startDateObj.getTime() - 14 * 24 * 60 * 60 * 1000);
          computedDateObj.setHours(0, 0, 0, 0);
          const computedDateStr = computedDateObj.toISOString().split('T')[0];
          const overdue = computedDateObj.getTime() < now.getTime() && s.status !== 'done' && s.status !== 'billed';
          return {
            date: computedDateStr,
            isComputed: true,
            label: 'Planung (-14 Tg.)',
            isOverdue: overdue
          };
        } catch {
          // Fallback
        }
      }
    }

    return {
      date: '',
      isComputed: false,
      label: 'Keine Frist',
      isOverdue: false
    };
  };

  // Convert legacy/normal projects to open position row representations
  const openPositions: OpenPositionRow[] = useMemo(() => {
    const list: OpenPositionRow[] = [];

    projects.forEach(p => {
      const meta = p.data?.metadata as ReportMetadata | undefined;
      const projectNum = p.project_number || meta?.projectNumber || 'Keine Nr.';
      const projName = p.name || meta?.projectName || 'Unbenannt';
      const projStart = p.start_date || meta?.globalData?.startDate || legacyMetaStartDate(meta) || '';
      
      const coordId = p.user_id || '';
      const coordUser = users.find(u => u.id === coordId);
      const coordName = coordUser 
        ? `${coordUser.first_name || ''} ${coordUser.last_name || ''}`.trim() 
        : 'System / Keine';

      const services = getNormalizedServices(p);
      const flatHints = flatCoverageHints(services);

      services.forEach((s: ServiceItem) => {
        // NEP ist nicht beauftragt — keine offene Position, die jemand
        // erledigen muesste. Dieselbe Regel wie in der Aufgabenliste
        // (TasksView legt sie unter „ausgeblendet" ab).
        if (isNepService(s)) return;
        const isOutstanding = s.status === 'open' || (!s.status && s.status !== 'done' && s.status !== 'billed');
        if (isOutstanding) {
          const dueResult = calculateDueDate(s, p);
          const flatHint = flatHints.get(s.id);
          list.push({
            id: s.id,
            lvPosition: s.lvPosition || 'N/A',
            title: s.title || '',
            projectNumber: projectNum,
            projectName: projName,
            coordinatorId: coordId,
            coordinatorName: coordName,
            startDate: projStart,
            dueDateObj: dueResult,
            // Gedeckt heisst: kein eigenes Geld — der Betrag steckt in der
            // Pauschalposition (siehe OpenPositionRow.flatHint).
            cost: flatHint ? 0 : contractedQuantity(s) * (Number(s.unitPrice) || 0),
            projectRaw: p,
            category: s.category,
            assignment: s.assignment,
            flatHint
          });
        }
      });
    });

    return list;
  }, [projects, users]);

  // Map all unique target items for selector list
  const allUniquePositionsMap = useMemo(() => {
    const map = new Map<string, { lv: string; title: string; count: number }>();
    openPositions.forEach(op => {
      if (op.lvPosition) {
        const existing = map.get(op.lvPosition);
        if (existing) {
          existing.count += 1;
        } else {
          map.set(op.lvPosition, { lv: op.lvPosition, title: op.title, count: 1 });
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => a.lv.localeCompare(b.lv, undefined, { numeric: true }));
  }, [openPositions]);

  // Map all unique coordinators with open positions
  const allUniqueCoordinatorsMap = useMemo(() => {
    const map = new Map<string, { id: string; name: string; count: number }>();
    openPositions.forEach(op => {
      const existing = map.get(op.coordinatorId);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(op.coordinatorId, { id: op.coordinatorId, name: op.coordinatorName, count: 1 });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [openPositions]);

  // Filter States
  const [selectedPositions, setSelectedPositions] = useRememberedState<string[]>('admin.open.positions', []);
  const tableScrollRef = useRememberedScroll('admin.open.table.scroll');
  const [selectedCoordinators, setSelectedCoordinators] = useRememberedState<string[]>('admin.open.coordinators', []);
  const [filterSearchTerm, setFilterSearchTerm] = useRememberedState('admin.open.search', '');
  const [coordinatorSearchTerm, setCoordinatorSearchTerm] = useRememberedState('admin.open.coordinatorSearch', '');
  const [onlyOverdueFilter, setOnlyOverdueFilter] = useRememberedState('admin.open.onlyOverdue', false);
  const [onlyPlanningFilter, setOnlyPlanningFilter] = useRememberedState('admin.open.onlyPlanning', false);
  
  // Sorting state
  const [sortKey, setSortKey] = useRememberedState<'lvPosition' | 'projectNumber' | 'projectName' | 'coordinatorName' | 'dueDate' | 'startDate' | 'cost'>('admin.open.sortKey', 'dueDate');
  const [sortDirection, setSortDirection] = useRememberedState<'asc' | 'desc'>('admin.open.sortDir', 'asc');

  // Position multi-select helper functions
  const handleTogglePosition = (lv: string) => {
    let current = [...selectedPositions];
    if (current.includes('__NONE_SELECTED__')) current = [];
    
    if (current.includes(lv)) {
      current = current.filter(x => x !== lv);
    } else {
      current.push(lv);
    }

    if (current.length === 0) {
      setSelectedPositions(['__NONE_SELECTED__']);
    } else {
      setSelectedPositions(current);
    }
  };

  const selectAllPositions = () => {
    setSelectedPositions([]);
  };

  const deselectAllPositions = () => {
    setSelectedPositions(['__NONE_SELECTED__']);
  };

  // Coordinator multi-select helper functions
  const handleToggleCoordinator = (id: string) => {
    let current = [...selectedCoordinators];
    if (current.includes('__NONE_SELECTED__')) current = [];

    if (current.includes(id)) {
      current = current.filter(x => x !== id);
    } else {
      current.push(id);
    }

    if (current.length === 0) {
      setSelectedCoordinators(['__NONE_SELECTED__']);
    } else {
      setSelectedCoordinators(current);
    }
  };

  const selectAllCoordinators = () => {
    setSelectedCoordinators([]);
  };

  const deselectAllCoordinators = () => {
    setSelectedCoordinators(['__NONE_SELECTED__']);
  };

  // Filter the positions list to display in select menu
  const displayUniquePositions = useMemo(() => {
    if (!filterSearchTerm) return allUniquePositionsMap;
    const lower = filterSearchTerm.toLowerCase();
    return allUniquePositionsMap.filter(item => 
      item.lv.toLowerCase().includes(lower) || 
      item.title.toLowerCase().includes(lower)
    );
  }, [allUniquePositionsMap, filterSearchTerm]);

  // Filter the coordinators list to display in select menu
  const displayUniqueCoordinators = useMemo(() => {
    if (!coordinatorSearchTerm) return allUniqueCoordinatorsMap;
    const lower = coordinatorSearchTerm.toLowerCase();
    return allUniqueCoordinatorsMap.filter(item => 
      item.name.toLowerCase().includes(lower)
    );
  }, [allUniqueCoordinatorsMap, coordinatorSearchTerm]);

  // Computed and filtered overall rows for displaying in main table
  const processedPositions = useMemo(() => {
    let list = [...openPositions];

    // Filter by position number selector
    if (selectedPositions.length > 0) {
      if (selectedPositions.includes('__NONE_SELECTED__')) {
        list = [];
      } else {
        list = list.filter(op => selectedPositions.includes(op.lvPosition));
      }
    }

    // Filter by coordinator selector
    if (selectedCoordinators.length > 0) {
      if (selectedCoordinators.includes('__NONE_SELECTED__')) {
        list = [];
      } else {
        list = list.filter(op => selectedCoordinators.includes(op.coordinatorId));
      }
    }

    // Filter by overdue flag
    if (onlyOverdueFilter) {
      list = list.filter(op => op.dueDateObj.isOverdue);
    }

    // Filter by planning category flag — dieselbe Liste wie bei der
    // Terminableitung oben, und aus demselben Grund OHNE `einweisung`: der
    // Filter heisst "nur Planungsleistungen", und eine Einweisung auf der
    // Baustelle ist keine. (Ihre Katalogposition traegt category
    // 'Ausführung' — sie faellt damit auch nicht ueber die Kategorie herein.)
    if (onlyPlanningFilter) {
      list = list.filter(op => istPlanungsleistung(op));
    }

    // Sorting
    list.sort((a, b) => {
      let valA: any = a[sortKey];
      let valB: any = b[sortKey];

      if (sortKey === 'dueDate') {
        const dateA = a.dueDateObj.date ? new Date(a.dueDateObj.date).getTime() : 9999999999999;
        const dateB = b.dueDateObj.date ? new Date(b.dueDateObj.date).getTime() : 9999999999999;
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }

      if (sortKey === 'startDate') {
        const dateA = a.startDate ? new Date(a.startDate).getTime() : 9999999999999;
        const dateB = b.startDate ? new Date(b.startDate).getTime() : 9999999999999;
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }

      if (sortKey === 'cost') {
        return sortDirection === 'asc' ? valA - valB : valB - valA;
      }

      // Default string comparison
      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
      
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [openPositions, selectedPositions, selectedCoordinators, onlyOverdueFilter, onlyPlanningFilter, sortKey, sortDirection]);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Render-Funktion statt Inline-Komponente: eine im Render neu erzeugte
  // Komponente bekaeme bei jedem Render eine neue Identitaet
  // (react-hooks/static-components) — als schlichter JSX-Helfer entfaellt das.
  const sortIcon = (colKey: typeof sortKey) => {
    if (sortKey !== colKey) return <ArrowUpDown className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 transition-colors" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-indigo-600" />
      : <ArrowDown className="w-3.5 h-3.5 text-indigo-600" />;
  };

  // PDF Export logic
  const handleExportPDF = (withPrice: boolean) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    
    // Title styling
    doc.setFillColor(30, 41, 59); // Dark Slate Blue (#1e293b)
    doc.rect(0, 0, 210, 38, 'F');
    
    // Title text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Sigeko Smart Report', 15, 16);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`Übersicht offene Leistungen und Fristen - Export ${withPrice ? 'mit Preise' : 'ohne Preise'}`, 15, 24);
    
    // Metadata block
    doc.setFontSize(8);
    doc.setTextColor(203, 213, 225); // Slate 300
    doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')} | Filter: ${
      selectedPositions.length === 0 ? 'Alle Positionen' : `${selectedPositions.length} ausgewählte`
    } | Koord: ${
      selectedCoordinators.length === 0 ? 'Alle Koordinatoren' : `${selectedCoordinators.length} ausgewählt`
    }`, 15, 32);

    // Dynamic Summary metrics in boxes
    doc.setFillColor(248, 250, 252); // Slate 50
    doc.setDrawColor(226, 232, 240); // Slate 200
    doc.roundedRect(15, 45, 55, 18, 2, 2, 'FD');
    doc.roundedRect(77, 45, 55, 18, 2, 2, 'FD');
    doc.roundedRect(140, 45, 55, 18, 2, 2, 'FD');

    doc.setTextColor(100, 116, 139); // Slate 500
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('OFFENE POSITIONEN', 18, 50);
    doc.text('ÜBERFÄLLIGE FRISTEN', 80, 50);
    doc.text(withPrice ? 'GESAMTBUDGET OFFEN' : 'GEPLANTE BAUSTARTS', 143, 50);

    doc.setTextColor(30, 41, 59); // Slate 800
    doc.setFontSize(12);
    doc.text(`${processedPositions.length}`, 18, 58);
    
    const overdueCount = processedPositions.filter(p => p.dueDateObj.isOverdue).length;
    doc.setFont('helvetica', 'bold');
    if (overdueCount > 0) {
      doc.setTextColor(220, 38, 38); // Red
    }
    doc.text(`${overdueCount}`, 80, 58);
    
    doc.setTextColor(30, 41, 59);
    if (withPrice) {
      const sum = processedPositions.reduce((s, x) => s + x.cost, 0);
      doc.setFont('helvetica', 'bold');
      doc.text(new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(sum), 143, 58);
    } else {
      const projsUnique = new Set(processedPositions.map(p => p.projectRaw.id)).size;
      doc.text(`${projsUnique} aktive Projekte`, 143, 58);
    }

    // Set font size for tables
    doc.setFont('helvetica', 'normal');
    
    const tableHeaders = withPrice 
      ? [['Pos.', 'Bezeichnung', 'Projekt', 'Koordinator', 'Fälligkeit', 'Kosten (€)']]
      : [['Pos.', 'Bezeichnung', 'Projekt', 'Koordinator', 'Fälligkeit']];

    const tableRows = processedPositions.map(pos => {
      const formattedDate = pos.dueDateObj.date 
        ? `${new Date(pos.dueDateObj.date).toLocaleDateString('de-DE')} (${pos.dueDateObj.label === 'Explizit' ? 'Expl.' : 'Plan (S-14)'})`
        : '-';

      const projectCell = pos.projectNumber && pos.projectNumber !== 'Keine Nr.'
        ? `${pos.projectName}\n(Projekt-Nr. ${pos.projectNumber})`
        : pos.projectName;

      const rowValue = [
        pos.lvPosition,
        // Der Pauschal-Hinweis muss mit aufs PDF: sonst laese der Empfaenger
        // den Strich in der Kostenspalte als Rechenfehler.
        pos.flatHint ? `${pos.title}\n${pos.flatHint}` : pos.title,
        projectCell,
        pos.coordinatorName,
        formattedDate
      ];

      if (withPrice) {
        rowValue.push(pos.flatHint
          ? '—'
          : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(pos.cost));
      }

      return rowValue;
    });

    autoTable(doc, {
      startY: 70,
      head: tableHeaders,
      body: tableRows,
      theme: 'grid',
      headStyles: {
        fillColor: [79, 70, 229], // Indigo 600
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'left'
      },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak'
      },
      columnStyles: withPrice 
        ? {
            0: { cellWidth: 15 }, // Pos.
            1: { cellWidth: 45 }, // Bez
            2: { cellWidth: 45 }, // Proj
            3: { cellWidth: 35 }, // Koord
            4: { cellWidth: 30 }, // Fälligkeit
            5: { cellWidth: 20, halign: 'right' } // Kosten
          }
        : {
            0: { cellWidth: 18 }, // Pos.
            1: { cellWidth: 55 }, // Bez
            2: { cellWidth: 55 }, // Proj
            3: { cellWidth: 35 }, // Koord
            4: { cellWidth: 27 }  // Fälligkeit
          },
      didParseCell: (data) => {
        // Highlight overdue deadlines with alert styling row or cell of Fälligkeit column (idx 4)
        if (data.section === 'body' && data.column.index === 4) {
          const rawRowIndex = data.row.index;
          const posObj = processedPositions[rawRowIndex];
          if (posObj && posObj.dueDateObj.isOverdue) {
            data.cell.styles.textColor = [220, 38, 38]; // Red 600
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    const finalY = (doc as any).lastAutoTable.finalY || 80;
    
    // Add signature/disclaimer text at bottom on same or new page
    if (finalY + 25 > 280) {
      doc.addPage();
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184); // Slate 400
      doc.text('Unterschrift Management / SiGeKo', 15, 30);
      doc.line(15, 45, 80, 45);
    } else {
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184); // Slate 400
      doc.text('Unterschrift Management / SiGeKo', 15, finalY + 15);
      doc.line(15, finalY + 25, 80, finalY + 25);
    }

    doc.save(`Sigeko_leistungsstand_offen_${withPrice ? 'mit_preise' : 'ohne_preise'}.pdf`);
  };

  return (
    <div data-glass-view="offene-positionen" className="space-y-6">
      {/* Read-Only Safety Banner */}
      <div className="bg-blue-50/50 border border-blue-200/60 rounded-2xl p-4 flex gap-3 items-center">
        <div className="p-2 bg-blue-100/60 text-blue-700 rounded-xl shrink-0">
          <BadgeCheck className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold text-blue-900">Sicherer Lese-Modus für offene Positionen</h4>
          <p className="text-xs text-blue-700/85">
            Alle in dieser Übersicht geöffneten Projekte werden im <strong>reinen Lese-Modus (View-Only)</strong> geöffnet. Modifikationen von Daten sind gesperrt, um unbeabsichtigte finanzielle Rechnungsabgänge zu verhindern.
          </p>
        </div>
        <div className="mr-2 hidden sm:flex gap-2">
          <button
            onClick={() => handleExportPDF(true)}
            className="text-xs font-bold px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-sm flex items-center gap-1.5 transition-all"
          >
            <FileDown className="w-3.5 h-3.5" /> mit Preise
          </button>
          <button
            onClick={() => handleExportPDF(false)}
            className="text-xs font-bold px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow-sm flex items-center gap-1.5 transition-all"
          >
            <FileDown className="w-3.5 h-3.5" /> ohne Preise
          </button>
        </div>
      </div>

      {/* Advanced Filter Control Center - Searchable, Multi-select checkboxes & Scrollable Panels */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 pb-2 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-indigo-500" />
            <h3 className="text-sm font-bold text-slate-700">Filterzentrum offene Leistungsverzeichnisse</h3>
          </div>
          
          {/* Mobile PDF export triggers (hidden on Desktop) */}
          <div className="flex gap-2 sm:hidden w-full">
            <button
              onClick={() => handleExportPDF(true)}
              className="flex-1 text-xs font-bold px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-sm flex items-center justify-center gap-1.5 transition-all"
            >
              <FileDown className="w-3.5 h-3.5" /> PDF mit Preisen
            </button>
            <button
              onClick={() => handleExportPDF(false)}
              className="flex-1 text-xs font-bold px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow-sm flex items-center justify-center gap-1.5 transition-all"
            >
              <FileDown className="w-3.5 h-3.5" /> PDF ohne Preise
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Column 1: Filter Presets & Deadlines */}
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                Positionsfilter suchen
              </label>
              <div className="relative">
                <input 
                  type="text"
                  placeholder="Pos.Nr. oder Bezeichnung..."
                  value={filterSearchTerm}
                  onChange={(e) => setFilterSearchTerm(e.target.value)}
                  className="w-full text-xs pl-8 pr-3 py-2 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-slate-50 transition-all"
                />
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
              </div>
            </div>

            <div className="space-y-2 pt-1 border-t border-slate-100">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                Schnellwahl Positionen
              </label>
              <div className="flex gap-2">
                <button 
                  onClick={selectAllPositions}
                  className={`flex-1 text-[11px] font-bold py-1.5 rounded-lg border text-center transition-all ${
                    selectedPositions.length === 0 
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-600 shadow-sm' 
                      : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  Alle {selectedPositions.length === 0 && '✓'}
                </button>
                <button 
                  onClick={deselectAllPositions}
                  className={`flex-1 text-[11px] font-bold py-1.5 rounded-lg border text-center transition-all ${
                    selectedPositions.includes('__NONE_SELECTED__')
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-600 shadow-sm'
                      : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  Keine {selectedPositions.includes('__NONE_SELECTED__') && '✓'}
                </button>
              </div>
            </div>

            <div className="space-y-2 pt-1.5 border-t border-slate-100">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                Frist-Zustände
              </label>
              <div className="flex flex-col gap-1.5">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700 hover:bg-slate-50 p-1.5 rounded-lg transition-colors">
                  <input 
                    type="checkbox" 
                    checked={onlyOverdueFilter} 
                    onChange={(e) => setOnlyOverdueFilter(e.target.checked)}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                  />
                  <span className="flex items-center gap-1.5 text-red-600">
                    <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                    Überfällige Fristen
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700 hover:bg-slate-50 p-1.5 rounded-lg transition-colors">
                  <input 
                    type="checkbox" 
                    checked={onlyPlanningFilter} 
                    onChange={(e) => setOnlyPlanningFilter(e.target.checked)}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                  />
                  <span className="flex items-center gap-1.5 text-amber-600">
                    <Clock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    Entwürfe / Planung
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Column 2: Scrollable Positions List */}
          <div className="flex flex-col border border-slate-200 rounded-2xl overflow-hidden bg-slate-50 lg:col-span-1">
            <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-100 flex justify-between items-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              <span>Position & Bezeichnung</span>
              <span>Offen</span>
            </div>

            <div className="overflow-y-auto h-[185px] p-2 bg-white divide-y divide-slate-100 scrollbar-thin">
              {displayUniquePositions.length === 0 ? (
                <div className="text-center py-10 text-slate-400 italic text-[11px]">
                  Keine Treffer
                </div>
              ) : (
                displayUniquePositions.map(item => {
                  const isChecked = selectedPositions.length === 0 || 
                    (selectedPositions.includes(item.lv) && !selectedPositions.includes('__NONE_SELECTED__'));

                  return (
                    <button
                      key={item.lv}
                      onClick={() => handleTogglePosition(item.lv)}
                      className="w-full text-left p-1.5 flex items-start gap-2 transition-colors hover:bg-slate-50 outline-none rounded"
                    >
                      <div className="pt-0.5 shrink-0">
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all ${
                          isChecked 
                            ? 'bg-indigo-600 border-indigo-600 text-white' 
                            : 'bg-white border-slate-300'
                        }`}>
                          <Check className="w-2.5 h-2.5 stroke-[3]" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-1 py-0.2 rounded shrink-0">
                            {item.lv}
                          </span>
                          <span className="text-[11px] font-bold text-slate-700 truncate block">
                            {item.title}
                          </span>
                        </div>
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 shrink-0">
                        {item.count}⨉
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Column 3: Coordinator Filter Input & presets */}
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                Koordinator suchen
              </label>
              <div className="relative">
                <input 
                  type="text"
                  placeholder="Koordinator filtern..."
                  value={coordinatorSearchTerm}
                  onChange={(e) => setCoordinatorSearchTerm(e.target.value)}
                  className="w-full text-xs pl-8 pr-3 py-2 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-slate-50 transition-all"
                />
                <User className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
              </div>
            </div>

            <div className="space-y-2 pt-1 border-t border-slate-100">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                Schnellwahl Koob.
              </label>
              <div className="flex gap-2">
                <button 
                  onClick={selectAllCoordinators}
                  className={`flex-1 text-[11px] font-bold py-1.5 rounded-lg border text-center transition-all ${
                    selectedCoordinators.length === 0 
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-600 shadow-sm' 
                      : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  Alle {selectedCoordinators.length === 0 && '✓'}
                </button>
                <button 
                  onClick={deselectAllCoordinators}
                  className={`flex-1 text-[11px] font-bold py-1.5 rounded-lg border text-center transition-all ${
                    selectedCoordinators.includes('__NONE_SELECTED__')
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-600 shadow-sm'
                      : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  Keine {selectedCoordinators.includes('__NONE_SELECTED__') && '✓'}
                </button>
              </div>
            </div>
          </div>

          {/* Column 4: Scrollable Coordinator List */}
          <div className="flex flex-col border border-slate-200 rounded-2xl overflow-hidden bg-slate-50">
            <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-100 flex justify-between items-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              <span>Koordinator Name</span>
              <span>Einträge</span>
            </div>

            <div className="overflow-y-auto h-[185px] p-2 bg-white divide-y divide-slate-100 scrollbar-thin">
              {displayUniqueCoordinators.length === 0 ? (
                <div className="text-center py-10 text-slate-400 italic text-[11px]">
                  Keine Treffer
                </div>
              ) : (
                displayUniqueCoordinators.map(item => {
                  const isChecked = selectedCoordinators.length === 0 || 
                    (selectedCoordinators.includes(item.id) && !selectedCoordinators.includes('__NONE_SELECTED__'));

                  return (
                    <button
                      key={item.id}
                      onClick={() => handleToggleCoordinator(item.id)}
                      className="w-full text-left p-1.5 flex items-center justify-between transition-colors hover:bg-slate-50 outline-none rounded"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all shrink-0 ${
                          isChecked 
                            ? 'bg-indigo-600 border-indigo-600 text-white' 
                            : 'bg-white border-slate-300'
                        }`}>
                          <Check className="w-2.5 h-2.5 stroke-[3]" />
                        </div>
                        <span className="text-[11px] font-bold text-slate-700 truncate block">
                          {item.name}
                        </span>
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 shrink-0 ml-1">
                        {item.count}⨉
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Dynamic calculation explanatory banner */}
        <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2 items-start text-[11px] text-slate-500">
          <Info className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            <strong>Fristenermittlung für Entwürfe/Planung (S-14 Tg.):</strong> Bei Leistungen der Entwurfsphase (z.B. SiGe-Plan, Vorankündigung oder Unterlage) ohne explizite Frist wird als Fälligkeit automatisch 14 Tage vor dem Baustart-Datum des Projekts gewählt.
          </p>
        </div>
      </div>

      {/* Positions Table Summary */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        {/* Scrollable Container with sticky header for high clarity */}
        <div ref={tableScrollRef} className="overflow-y-auto max-h-[500px] overflow-x-auto">
          <table className="w-full text-left border-collapse table-fixed min-w-[1050px]">
            <thead className="sticky top-0 z-[10] bg-slate-50/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
              <tr className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                {/* Positionsnummer Header */}
                <th className="px-5 py-4 w-[22%]">
                  <button onClick={() => handleSort('lvPosition')} className="flex items-center gap-1.5 group font-bold tracking-wider">
                    Position & Bezeichnung {sortIcon('lvPosition')}
                  </button>
                </th>
                {/* Projekt & Koordinator Header */}
                <th className="px-4 py-4 w-[28%]">
                  <button onClick={() => handleSort('projectName')} className="flex items-center gap-1.5 group font-bold tracking-wider">
                    Projekt & Koordinator {sortIcon('projectName')}
                  </button>
                </th>
                {/* Baustart Header (crucial to show -14 days logic context) */}
                <th className="px-4 py-4 w-[16%]">
                  <button onClick={() => handleSort('startDate')} className="flex items-center gap-1.5 group font-bold tracking-wider">
                    Projektauftakt {sortIcon('startDate')}
                  </button>
                </th>
                {/* Fälligkeit Header */}
                <th className="px-4 py-4 w-[20%]">
                  <button onClick={() => handleSort('dueDate')} className="flex items-center gap-1.5 group font-bold tracking-wider">
                    Planungs-Deadline {sortIcon('dueDate')}
                  </button>
                </th>
                {/* Kosten Header */}
                <th className="px-5 py-4 w-[14%] text-right">
                  <button onClick={() => handleSort('cost')} className="flex items-center gap-1.5 justify-end group font-bold tracking-wider w-full">
                    Kosten (€) {sortIcon('cost')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {processedPositions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-slate-400 italic bg-slate-50/20">
                    <ShieldAlert className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    Keine passenden offenen Positionen in der Übersicht vorhanden.
                  </td>
                </tr>
              ) : (
                processedPositions.map(pos => {
                  const hasDeadline = !!pos.dueDateObj.date;
                  const isOverdue = pos.dueDateObj.isOverdue;

                  return (
                    <tr 
                      key={`${pos.projectRaw.id}-${pos.id}`} 
                      onClick={() => onOpenProject(pos.projectRaw, true)}
                      className="hover:bg-indigo-50/40 transition-all cursor-pointer group"
                    >
                      {/* Pos & Title */}
                      <td className="px-5 py-3.5 align-top">
                        <div className="space-y-1">
                          <span className="font-mono text-xs font-bold bg-indigo-50 border border-indigo-100 text-indigo-700 px-2.5 py-1 rounded-lg inline-block">
                            {pos.lvPosition}
                          </span>
                          <div className="text-xs font-extrabold text-slate-800 break-words line-clamp-2" title={pos.title}>
                            {pos.title}
                          </div>
                          {pos.flatHint && (
                            <div className="text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5 inline-block" title={pos.flatHint}>
                              {pos.flatHint}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Project info combined */}
                      <td className="px-4 py-3.5 align-top space-y-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-bold text-slate-800 line-clamp-1 group-hover:text-indigo-600 transition-colors" title={pos.projectName}>
                            {pos.projectName}
                          </span>
                          <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-600 transition-colors shrink-0" />
                        </div>
                        <div className="text-[11px] text-slate-400 font-mono">
                          Pro-Nr: {pos.projectNumber || '-'}
                        </div>
                        <div className="text-[11px] text-slate-500 font-medium">
                          Koord: {pos.coordinatorName}
                        </div>
                      </td>

                      {/* Project Start Date */}
                      <td className="px-4 py-3.5 text-xs text-slate-600 font-semibold align-top space-y-1">
                        <div className="flex items-center gap-1.5 text-slate-600 pt-0.5">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          <span>
                            {pos.startDate ? (
                              new Date(pos.startDate).toLocaleDateString('de-DE', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric' 
                              })
                            ) : (
                              <span className="text-slate-300 italic">Nicht festgelegt</span>
                            )}
                          </span>
                        </div>
                        {pos.startDate && (
                          <span className="text-[11px] text-slate-400 block italic">Soll-Baustart</span>
                        )}
                      </td>

                      {/* Computed/Explicit DueDate and Warnings */}
                      <td className="px-4 py-3.5 align-top space-y-2">
                        {hasDeadline ? (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className={`text-xs font-bold font-mono px-2 py-0.5 rounded-md border ${
                                isOverdue 
                                  ? 'bg-red-50 text-red-700 border-red-200' 
                                  : 'bg-emerald-50 text-emerald-800 border-emerald-200'
                              }`}>
                                {new Date(pos.dueDateObj.date).toLocaleDateString('de-DE', { 
                                  day: '2-digit', 
                                  month: '2-digit', 
                                  year: 'numeric' 
                                })}
                              </span>

                              {isOverdue && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-800 border border-red-200 uppercase tracking-wide animate-pulse">
                                  <AlertCircle className="w-3 h-3 text-red-600 shrink-0" /> Offen
                                </span>
                              )}
                            </div>

                            <span className="text-[11px] text-slate-400 block font-medium">
                              Typ: {pos.dueDateObj.label}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-300 italic text-xs block pt-1">Keine Frist vorhanden</span>
                        )}
                      </td>

                      {/* Financial Value — gedeckte Positionen tragen kein eigenes Geld */}
                      <td className="px-5 py-3.5 font-mono text-sm font-bold text-slate-700 text-right align-top">
                        {pos.flatHint
                          ? <span className="text-slate-300" title={pos.flatHint}>—</span>
                          : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(pos.cost)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {processedPositions.length > 0 && (
          <div className="bg-slate-50 px-5 py-4 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-2">
            <span className="text-xs text-slate-500 font-medium font-bold">
              Gefundene offene Einträge: <strong className="text-slate-700">{processedPositions.length}</strong>
            </span>
            <span className="text-xs text-slate-500 font-semibold font-bold">
              Gesamtwert dieser Positionen: <strong className="text-indigo-600 font-mono text-base ml-1">{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(processedPositions.reduce((s, x) => s + x.cost, 0))}</strong>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
